import { useState } from "react";
import { Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import { Images } from "../../../utils/Images";

const fallbackFaqData = [
  {
    question: "How does buying from a vendor help an NGO?",
    answer:
      "When you purchase a product from a featured vendor, the vendor prepares and delivers that product to one of our partner NGOs (orphanage, old age home, shelter, etc.). The vendor earns income from the sale, and the NGO receives essential goods - creating a dual impact from a single purchase.",
  },
  {
    question: "How are vendors selected and verified?",
    answer:
      "Our team verifies each nominated vendor through local checks, product quality reviews, and background validation before they are featured on the platform.",
  },
  {
    question: "Can I choose which NGO receives the products?",
    answer:
      "At the moment, distribution is managed through our verified NGO network to ensure fair allocation based on real-time needs across partner organizations.",
  },
  {
    question: "Is my donation/purchase tax-deductible?",
    answer:
      "Tax benefits depend on the campaign structure and applicable laws. If eligible, details and certificates are shared through the proper donation flow.",
  },
];

const FaqSection = () => {
  const isMobile = useMediaQuery("(max-width: 899px)");
  const isSmallMobile = useMediaQuery("(max-width: 599.95px)");
  const home = useSelector((state) => state.cms.home);

  const sections = Array.isArray(home?.data) ? home.data : [];
  const faqCms = sections.find((item) => {
    if (!item) return false;
    const isActive = item.status === 1 || item.status === true;
    if (!isActive) return false;
    return String(item.title || "").toLowerCase().includes("small vendors") && String(item.title || "").toLowerCase().includes("frequently asked questions");
  });

  const faqTitle = faqCms?.title || "Small Vendors - Frequently Asked Questions";

  const cmsFaqData = Array.isArray(faqCms?.relatedCMS)
    ? faqCms.relatedCMS
        .filter((item) => item && (item.status === 1 || item.status === true))
        .map((item) => ({
          question: item?.title || "",
          answer: item?.description || "",
        }))
    : [];

  const faqData = cmsFaqData.length > 0 ? cmsFaqData : fallbackFaqData;

  const [openItems, setOpenItems] = useState([0]);

  const toggleItem = (index) => {
    setOpenItems((prev) =>
      prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]
    );
  };

  return (
    <Box
      sx={{
        width: isSmallMobile ? "100%" : "94.44%",
        maxWidth: "1440px",
        mx: "auto",
        px: isSmallMobile ? "16px" : 0,
        mb: isMobile ? "30px" : "60px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          background: "#FFFFFF",
          borderRadius: isSmallMobile ? "0px" : "20.438px",
          border: isMobile ? "none" : "1.022px solid #EAEAF0",
          px: isMobile ? "0px" : "44px",
          py: isMobile ? "0px" : "40px",
          display: "flex",
          flexDirection: "column",
          gap: isSmallMobile ? "18px" : isMobile ? "20px" : "40px",
        }}
      >
        <Typography
          align={isSmallMobile ? "left" : "center"}
          sx={{
            fontWeight: 700,
            fontSize: isSmallMobile ? "14px" : isMobile ? "18px" : "24px",
            lineHeight: isSmallMobile ? "13.122px" : 1,
            letterSpacing: isSmallMobile ? "-0.28px" : "-0.48px",
            color: "#1D1D1B",
          }}
        >
          {faqTitle}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: isSmallMobile ? "11.927px" : isMobile ? "16px" : "26px", width: "100%" }}>
          {faqData.map((item, index) => (
            <Box
              key={item.question}
              sx={{
                background: "#FFFFFF",
                borderRadius: isSmallMobile ? "5.964px" : "12px",
                boxShadow: isSmallMobile ? "0px 2.982px 9.542px 0px rgba(8, 15, 52, 0.06)" : "0px 5px 16px 0px rgba(8, 15, 52, 0.06)",
                px: isMobile ? "16px" : "24px",
                py: isMobile ? "18px" : "25px",
              }}
            >
              <Box
                onClick={() => toggleItem(index)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  gap: "16px",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: isSmallMobile ? 600 : 700,
                    fontSize: isSmallMobile ? "12px" : isMobile ? "14px" : index === 0 ? "18px" : "16px",
                    lineHeight: isSmallMobile ? (openItems.includes(index) ? "10.735px" : "9.542px") : isMobile ? "20px" : index === 0 ? "18px" : "16px",
                    color: "#1D1D1B",
                  }}
                >
                  {item.question}
                </Typography>

                <IconButton
                  disableRipple
                  sx={{
                    width: isSmallMobile ? (openItems.includes(index) ? "15.505px" : "20px") : "26px",
                    height: isSmallMobile ? (openItems.includes(index) ? "15.505px" : "20px") : "26px",
                    p: 0,
                    borderRadius: openItems.includes(index) ? "4px" : "2.989px",
                    background: openItems.includes(index) ? "#29CC6A" : "#F5F8ED",
                  }}
                >
                  <Box
                    component="img"
                    src={openItems.includes(index) ? Images.minusIcon : Images.plusIcon}
                    alt="toggle"
                    sx={{ width: isSmallMobile ? "6.725px" : "11px", height: isSmallMobile ? "6.725px" : "11px" }}
                  />
                </IconButton>
              </Box>

              {openItems.includes(index) && (
                <Typography
                  sx={{
                    mt: isSmallMobile ? "5.964px" : "14px",
                    fontWeight: 400,
                    fontSize: isSmallMobile ? "10px" : "14px",
                    lineHeight: isSmallMobile ? "16.18px" : "22.652px",
                    color: isSmallMobile ? "#686866" : "#6F6C90",
                    maxWidth: isMobile ? "100%" : "1113px",
                  }}
                >
                  {item.answer}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default FaqSection;
